/**
 * Ring 2 Effectiveness Dashboard (Phase 25.6)
 *
 * Admin-only (super_admin + admin) ops page for monitoring the health of
 * Ring 2 (cohort-level) training fan-outs. Consumes
 * `/api/analytics/ring-2-effectiveness`.
 *
 * Deliberately minimal visual design — this is an operations tool, not a
 * customer-facing surface. Prioritizes data legibility and quick scanning
 * over polish.
 *
 * Charts:
 *   1. Completion-rate line chart over time (refresher vs focused_drill)
 *   2. Section breakdown table with canary amber pill for <70% completion
 *      where volume exceeds the min-assignment threshold
 *   3. Shift breakdown table, same canary treatment
 *   4. Stacked bar: skipped fan-outs by reason
 *
 * Canary thresholds are set on the server and passed through as `canary` — the
 * UI just renders them (keeps the threshold a single source of truth).
 */

import { useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Info,
  Loader2,
  Shield,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  useRing2Effectiveness,
  type Ring2SectionRow,
  type Ring2ShiftRow,
  type Ring2SkipReasonRow,
  type Ring2TimeSeriesPoint,
} from '../../hooks/useRing2Effectiveness';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { cn } from '../../lib/utils';

type Preset = '7d' | '30d' | '90d';

const PRESETS: { value: Preset; label: string; days: number }[] = [
  { value: '7d', label: '7 Days', days: 7 },
  { value: '30d', label: '30 Days', days: 30 },
  { value: '90d', label: '90 Days', days: 90 },
];

// Human-readable labels for server-side skip reasons. Fallback is the raw
// reason string from the event log — new reasons surface automatically.
const REASON_LABEL: Record<string, string> = {
  config_disabled: 'Config disabled',
  no_section: 'No section',
  no_shift_or_station: 'Missing shift/station',
  no_task: 'No task',
  cooldown: 'Cooldown',
  no_content: 'No content',
  below_k_threshold: 'Below k-anonymity threshold',
  config_disabled_at_fire: 'Disabled at fire (worker)',
  material_unavailable_at_fire: 'Material gone at fire (worker)',
  no_peers: 'No peers at fire (worker)',
  unknown: 'Unknown',
};

function formatPercent(value: number | null): string {
  if (value == null || !isFinite(value)) return '--';
  return `${(value * 100).toFixed(1)}%`;
}

function labelForReason(reason: string): string {
  return REASON_LABEL[reason] ?? reason;
}

function computeDateRange(preset: Preset): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  const days = PRESETS.find((p) => p.value === preset)?.days ?? 30;
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

export function Ring2EffectivenessPage() {
  useDocumentTitle('Ring 2 Effectiveness');
  const [preset, setPreset] = useState<Preset>('30d');

  const dateRange = useMemo(() => computeDateRange(preset), [preset]);
  const { data, isLoading, isError } = useRing2Effectiveness(dateRange);

  // ---------- Derived series for the completion-rate chart. ----------
  // Recharts expects flat row-per-date. We compute completion rate per
  // variant here instead of sending three series from the server.
  const completionSeries = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map((pt: Ring2TimeSeriesPoint) => ({
      date: pt.date,
      refresherRate:
        pt.refresherAssigned === 0
          ? null
          : (pt.refresherCompleted / pt.refresherAssigned) * 100,
      focusedDrillRate:
        pt.focusedDrillAssigned === 0
          ? null
          : (pt.focusedDrillCompleted / pt.focusedDrillAssigned) * 100,
      refresherAssigned: pt.refresherAssigned,
      focusedDrillAssigned: pt.focusedDrillAssigned,
    }));
  }, [data?.series]);

  // ---------- Loading + empty states. ----------
  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        Failed to load Ring 2 effectiveness data.
      </div>
    );
  }

  const hasActivity =
    data.summary.totalAssigned > 0 || data.skippedByReason.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-guild-green/10 text-guild-green">
            <Activity size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Ring 2 Effectiveness</h1>
            <p className="text-sm text-gray-500">
              Cohort-level fan-out health — completion rate, canary flags, skip reasons
            </p>
          </div>
        </div>

        {/* Date-range preset toggle */}
        <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPreset(p.value)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200',
                preset === p.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Empty-state card */}
      {!hasActivity && (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
          <Shield size={32} className="mx-auto mb-3 text-gray-300" />
          <h3 className="text-sm font-medium text-gray-700">
            No Ring 2 activity in this period
          </h3>
          <p className="mx-auto mt-1 max-w-md text-xs text-gray-500">
            This is expected if no low-score incidents have fired cohort training.
            Try widening the date range, or check that
            <code className="mx-1 rounded bg-gray-100 px-1">TRAINING_DISTRIBUTION_CONFIG.ENABLED</code>
            is on.
          </p>
        </div>
      )}

      {hasActivity && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <SummaryTile
              label="Total assigned"
              value={data.summary.totalAssigned.toLocaleString()}
              sublabel="Ring 2 peer assignments"
            />
            <SummaryTile
              label="Total completed"
              value={data.summary.totalCompleted.toLocaleString()}
              sublabel={`of ${data.summary.totalAssigned.toLocaleString()}`}
            />
            <SummaryTile
              label="Overall completion"
              value={formatPercent(data.summary.overallCompletionRate)}
              sublabel="Across all cohorts"
            />
            <SummaryTile
              label="Canary flags"
              value={`${data.summary.canarySectionCount + data.summary.canaryShiftCount}`}
              sublabel={`${data.summary.canarySectionCount} section · ${data.summary.canaryShiftCount} shift`}
              highlight={
                data.summary.canarySectionCount + data.summary.canaryShiftCount > 0
              }
            />
          </div>

          {/* Known-limitations tooltip (blueprint §25.6 step 5) */}
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <Info size={14} className="mt-0.5 flex-none" />
            <div>
              <span className="font-medium">Known limitation:</span> Completion rate
              counts all peers who were invited, including those on PTO or who went
              inactive before the due date. A dip under
              {' '}
              {formatPercent(data.canary.completionThreshold)}
              {' '}
              can reflect coverage gaps rather than engagement problems. For
              low-confidence rows, drill into the peer list to identify inactive
              participants.
            </div>
          </div>

          {/* Completion rate over time */}
          <Card
            title="Completion rate over time"
            subtitle="Daily rate per variant (refresher vs focused drill)"
          >
            {completionSeries.length === 0 ? (
              <EmptyChart />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={completionSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip
                      formatter={(value) =>
                        typeof value === 'number' ? `${value.toFixed(1)}%` : '--'
                      }
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="refresherRate"
                      name="Refresher"
                      stroke="#00674a"
                      strokeWidth={2}
                      connectNulls
                    />
                    <Line
                      type="monotone"
                      dataKey="focusedDrillRate"
                      name="Focused Drill"
                      stroke="#b45309"
                      strokeWidth={2}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* Section breakdown */}
          <Card
            title="Section breakdown"
            subtitle="Top 10 sections by Ring 2 volume"
          >
            {data.sectionBreakdown.length === 0 ? (
              <EmptyChart />
            ) : (
              <SectionTable rows={data.sectionBreakdown} />
            )}
          </Card>

          {/* Shift breakdown */}
          <Card
            title="Shift breakdown"
            subtitle="All shifts with Ring 2 activity"
          >
            {data.shiftBreakdown.length === 0 ? (
              <EmptyChart />
            ) : (
              <ShiftTable rows={data.shiftBreakdown} />
            )}
          </Card>

          {/* Skipped by reason */}
          <Card
            title="Skipped fan-outs by reason"
            subtitle="Why Ring 2 did not fire (gate rejects + worker-time skips)"
          >
            {data.skippedByReason.length === 0 ? (
              <EmptyChart />
            ) : (
              <SkippedChart rows={data.skippedByReason} />
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-5 py-3">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  sublabel,
  highlight = false,
}: {
  label: string;
  value: string;
  sublabel?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border bg-white p-4',
        highlight ? 'border-amber-300' : 'border-gray-200'
      )}
    >
      <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
      <p
        className={cn(
          'mt-1 text-2xl font-bold tabular-nums',
          highlight ? 'text-amber-700' : 'text-gray-900'
        )}
      >
        {value}
      </p>
      {sublabel && <p className="mt-0.5 text-xs text-gray-500">{sublabel}</p>}
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-24 items-center justify-center text-xs text-gray-400">
      No data in this window.
    </div>
  );
}

function CanaryPill() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      <AlertTriangle size={10} />
      Canary
    </span>
  );
}

function SectionTable({ rows }: { rows: Ring2SectionRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="py-2 pr-4 text-left font-medium">Section</th>
            <th className="py-2 pr-4 text-left font-medium">SOP</th>
            <th className="py-2 pr-4 text-right font-medium">Assigned</th>
            <th className="py-2 pr-4 text-right font-medium">Completed</th>
            <th className="py-2 pr-4 text-right font-medium">Rate</th>
            <th className="py-2 text-left font-medium">Flag</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.sectionId} className={row.isCanary ? 'bg-amber-50/50' : ''}>
              <td className="py-2 pr-4 font-medium text-gray-900">
                {row.sectionTitle ?? '—'}
              </td>
              <td className="py-2 pr-4 text-gray-600">{row.sopTitle ?? '—'}</td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {row.assignedCount}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {row.completedCount}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums font-semibold">
                {formatPercent(row.completionRate)}
              </td>
              <td className="py-2">{row.isCanary && <CanaryPill />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShiftTable({ rows }: { rows: Ring2ShiftRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="py-2 pr-4 text-left font-medium">Shift</th>
            <th className="py-2 pr-4 text-right font-medium">Assigned</th>
            <th className="py-2 pr-4 text-right font-medium">Completed</th>
            <th className="py-2 pr-4 text-right font-medium">Rate</th>
            <th className="py-2 text-left font-medium">Flag</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.shiftId} className={row.isCanary ? 'bg-amber-50/50' : ''}>
              <td className="py-2 pr-4 font-medium text-gray-900">
                {row.shiftName ?? '—'}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {row.assignedCount}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {row.completedCount}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums font-semibold">
                {formatPercent(row.completionRate)}
              </td>
              <td className="py-2">{row.isCanary && <CanaryPill />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SkippedChart({ rows }: { rows: Ring2SkipReasonRow[] }) {
  const chartData = useMemo(
    () =>
      rows.map((r) => ({
        reason: labelForReason(r.reason),
        count: r.count,
      })),
    [rows]
  );

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="reason"
            tick={{ fontSize: 11 }}
            width={180}
          />
          <Tooltip />
          <Bar dataKey="count" fill="#00674a" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default Ring2EffectivenessPage;
